import type { CompanionState } from '../companion/ScoutCompanion';
import type { FcStatus } from '../fc/FCHost';
import type { SetpointKind } from '../fc/setpoints';
import type { SimState } from '../sim/Scenario';
import type { Quat, Vec3 } from '../sim/mathx';
import type { TrackState } from '../sim/trackTypes';
import {
  MAX_TARGET_ID_BYTES,
  MAX_TARGETS,
  MAX_TRACKS,
  TARGET_RECORD_BYTES,
  TELEMETRY_BUFFER_SIZE,
  TELEMETRY_HEADER_BYTES,
  TELEMETRY_SLOT_BYTES,
  TRACK_RECORD_BYTES,
  TargetRecordOffset,
  TelemetryHeaderOffset,
  TelemetrySlotOffset,
  TrackRecordOffset,
  telemetrySlotByteOffset,
  type StateBuffer,
} from './layout';

export const DEFAULT_SEQLOCK_RETRIES = 8;

const RPM_PER_RADIAN_PER_SECOND = 30 / Math.PI;
const NULL_TRACK_ID = -1;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder('utf-8', { fatal: true });

const FC_MODES = ['idle', 'acro', 'angle', 'rate', 'crashed'] as const;
const SETPOINT_KINDS = [
  'idle',
  'manual',
  'goto',
  'loiter',
] as const satisfies readonly SetpointKind[];
const COMPANION_STATES = [
  'IDLE',
  'SLEWING',
  'ON_STATION',
  'TARGET_LOST',
  'RTL',
] as const satisfies readonly CompanionState[];
const TRACK_STATES = [
  'tentative',
  'confirmed',
  'coasting',
  'dropped',
] as const satisfies readonly TrackState[];

const FcFlag = Object.freeze({
  armed: 1 << 0,
  saturated: 1 << 1,
  crashed: 1 << 2,
  manualLatched: 1 << 3,
});

export interface TelemetryTarget {
  id: string;
  posEnu: Vec3;
}

export interface TelemetryTrack {
  id: number;
  state: TrackState;
  posEnu: Vec3;
  velEnu: Vec3;
}

export interface TelemetrySnapshot {
  time: number;
  scout: {
    posEnu: Vec3;
    attitude: Quat;
    velEnu: Vec3;
    angVel: Vec3;
    batteryV: number;
    motorRpm: [number, number, number, number];
    flightController: FcStatus;
  };
  targets: TelemetryTarget[];
  tracks: TelemetryTrack[];
  companion: {
    state: CompanionState;
    selectedTrackId: number | null;
  };
  unstable: boolean;
  /** FNV-1a checksum of every slot byte before this field. */
  checksum: number;
}

export interface TelemetryBufferOptions {
  maxReadRetries?: number;
}

const enumIndex = (values: readonly string[], value: string, field: string): number => {
  const index = values.indexOf(value);
  if (index < 0) throw new RangeError(`${field} has unsupported value: ${value}`);
  return index;
};

const enumValue = <T extends string>(values: readonly T[], index: number, field: string): T => {
  const value = values[index];
  if (value === undefined) throw new RangeError(`${field} has invalid enum value: ${index}`);
  return value;
};

const assertInt32 = (value: number, field: string): void => {
  if (!Number.isInteger(value) || value < -0x8000_0000 || value > 0x7fff_ffff) {
    throw new RangeError(`${field} must be a signed 32-bit integer`);
  }
};

const writeVec3 = (view: DataView, offset: number, value: Vec3): void => {
  view.setFloat64(offset, value.x, true);
  view.setFloat64(offset + 8, value.y, true);
  view.setFloat64(offset + 16, value.z, true);
};

const readVec3 = (view: DataView, offset: number): Vec3 => ({
  x: view.getFloat64(offset, true),
  y: view.getFloat64(offset + 8, true),
  z: view.getFloat64(offset + 16, true),
});

const writeTargetId = (bytes: Uint8Array, offset: number, id: string): void => {
  const encoded = textEncoder.encode(id);
  if (encoded.byteLength >= MAX_TARGET_ID_BYTES) {
    throw new RangeError(`target id must fit in ${MAX_TARGET_ID_BYTES - 1} UTF-8 bytes`);
  }
  bytes[offset] = encoded.byteLength;
  bytes.set(encoded, offset + 1);
};

const readTargetId = (bytes: Uint8Array, offset: number): string => {
  const length = bytes[offset] ?? 0;
  if (length >= MAX_TARGET_ID_BYTES) throw new RangeError('target id length is corrupt');
  return textDecoder.decode(bytes.subarray(offset + 1, offset + 1 + length));
};

const fcFlags = (status: FcStatus): number =>
  (status.armed ? FcFlag.armed : 0) |
  (status.saturated ? FcFlag.saturated : 0) |
  (status.crashed ? FcFlag.crashed : 0) |
  (status.manualLatched ? FcFlag.manualLatched : 0);

const fnv1a = (bytes: Uint8Array): number => {
  let hash = 0x811c_9dc5;
  for (const byte of bytes) hash = Math.imul(hash ^ byte, 0x0100_0193);
  return hash >>> 0;
};

const encodePayload = (view: DataView, bytes: Uint8Array, snapshot: TelemetrySnapshot): void => {
  view.setFloat64(TelemetrySlotOffset.time, snapshot.time, true);
  writeVec3(view, TelemetrySlotOffset.scoutPos, snapshot.scout.posEnu);
  const quat = snapshot.scout.attitude;
  view.setFloat64(TelemetrySlotOffset.scoutQuat, quat.x, true);
  view.setFloat64(TelemetrySlotOffset.scoutQuat + 8, quat.y, true);
  view.setFloat64(TelemetrySlotOffset.scoutQuat + 16, quat.z, true);
  view.setFloat64(TelemetrySlotOffset.scoutQuat + 24, quat.w, true);
  writeVec3(view, TelemetrySlotOffset.scoutVel, snapshot.scout.velEnu);
  writeVec3(view, TelemetrySlotOffset.scoutAngVel, snapshot.scout.angVel);
  view.setFloat64(TelemetrySlotOffset.batteryV, snapshot.scout.batteryV, true);
  for (let motor = 0; motor < 4; motor += 1) {
    view.setFloat64(
      TelemetrySlotOffset.motorRpm + motor * 8,
      snapshot.scout.motorRpm[motor] ?? 0,
      true,
    );
  }

  const fc = snapshot.scout.flightController;
  view.setUint32(TelemetrySlotOffset.fcFlags, fcFlags(fc), true);
  view.setInt32(TelemetrySlotOffset.fcMode, enumIndex(FC_MODES, fc.mode, 'FC mode'), true);
  view.setInt32(
    TelemetrySlotOffset.activeSetpointKind,
    enumIndex(SETPOINT_KINDS, fc.activeSetpointKind, 'active setpoint kind'),
    true,
  );
  view.setInt32(
    TelemetrySlotOffset.companionState,
    enumIndex(COMPANION_STATES, snapshot.companion.state, 'companion state'),
    true,
  );
  const selectedTrackId = snapshot.companion.selectedTrackId ?? NULL_TRACK_ID;
  assertInt32(selectedTrackId, 'selected track id');
  view.setInt32(TelemetrySlotOffset.selectedTrackId, selectedTrackId, true);
  view.setUint32(TelemetrySlotOffset.unstable, snapshot.unstable ? 1 : 0, true);

  const targets = snapshot.targets.slice(0, MAX_TARGETS);
  const tracks = snapshot.tracks.slice(0, MAX_TRACKS);
  view.setUint32(TelemetrySlotOffset.targetCount, targets.length, true);
  view.setUint32(TelemetrySlotOffset.trackCount, tracks.length, true);
  targets.forEach((target, index) => {
    const offset = TelemetrySlotOffset.targets + index * TARGET_RECORD_BYTES;
    writeTargetId(bytes, offset, target.id);
    writeVec3(view, offset + TargetRecordOffset.pos, target.posEnu);
  });
  tracks.forEach((track, index) => {
    assertInt32(track.id, `track[${index}].id`);
    const offset = TelemetrySlotOffset.tracks + index * TRACK_RECORD_BYTES;
    view.setInt32(offset + TrackRecordOffset.id, track.id, true);
    view.setInt32(
      offset + TrackRecordOffset.state,
      enumIndex(TRACK_STATES, track.state, 'track state'),
      true,
    );
    writeVec3(view, offset + TrackRecordOffset.pos, track.posEnu);
    writeVec3(view, offset + TrackRecordOffset.vel, track.velEnu);
  });
};

const snapshotFromState = (state: SimState): TelemetrySnapshot => ({
  time: state.time,
  scout: {
    posEnu: { ...state.scout.posEnu },
    attitude: { ...state.scout.attitude },
    velEnu: { ...state.scout.velEnu },
    angVel: { ...state.scout.body.angVel },
    batteryV: state.scout.batteryV,
    motorRpm: [0, 1, 2, 3].map(
      (motor) => (state.scout.vehicle.motorOmega[motor] ?? 0) * RPM_PER_RADIAN_PER_SECOND,
    ) as [number, number, number, number],
    flightController: { ...state.scout.flightController },
  },
  targets: state.targets.map((target) => ({ id: target.id, posEnu: { ...target.posEnu } })),
  tracks: state.tracks.map((track) => ({
    id: track.id,
    state: track.state,
    posEnu: { ...track.posEnu },
    velEnu: { ...track.velEnu },
  })),
  companion: {
    state: state.companion.state,
    selectedTrackId: state.companion.selectedTrackId,
  },
  unstable: state.unstable,
  checksum: 0,
});

const decodePayload = (view: DataView, bytes: Uint8Array): TelemetrySnapshot => {
  const flags = view.getUint32(TelemetrySlotOffset.fcFlags, true);
  const targetCount = view.getUint32(TelemetrySlotOffset.targetCount, true);
  const trackCount = view.getUint32(TelemetrySlotOffset.trackCount, true);
  if (targetCount > MAX_TARGETS || trackCount > MAX_TRACKS) {
    throw new RangeError('telemetry collection count is corrupt');
  }
  const targets: TelemetryTarget[] = [];
  for (let index = 0; index < targetCount; index += 1) {
    const offset = TelemetrySlotOffset.targets + index * TARGET_RECORD_BYTES;
    targets.push({
      id: readTargetId(bytes, offset),
      posEnu: readVec3(view, offset + TargetRecordOffset.pos),
    });
  }
  const tracks: TelemetryTrack[] = [];
  for (let index = 0; index < trackCount; index += 1) {
    const offset = TelemetrySlotOffset.tracks + index * TRACK_RECORD_BYTES;
    tracks.push({
      id: view.getInt32(offset + TrackRecordOffset.id, true),
      state: enumValue(
        TRACK_STATES,
        view.getInt32(offset + TrackRecordOffset.state, true),
        'track state',
      ),
      posEnu: readVec3(view, offset + TrackRecordOffset.pos),
      velEnu: readVec3(view, offset + TrackRecordOffset.vel),
    });
  }
  const selectedTrackId = view.getInt32(TelemetrySlotOffset.selectedTrackId, true);
  return {
    time: view.getFloat64(TelemetrySlotOffset.time, true),
    scout: {
      posEnu: readVec3(view, TelemetrySlotOffset.scoutPos),
      attitude: {
        x: view.getFloat64(TelemetrySlotOffset.scoutQuat, true),
        y: view.getFloat64(TelemetrySlotOffset.scoutQuat + 8, true),
        z: view.getFloat64(TelemetrySlotOffset.scoutQuat + 16, true),
        w: view.getFloat64(TelemetrySlotOffset.scoutQuat + 24, true),
      },
      velEnu: readVec3(view, TelemetrySlotOffset.scoutVel),
      angVel: readVec3(view, TelemetrySlotOffset.scoutAngVel),
      batteryV: view.getFloat64(TelemetrySlotOffset.batteryV, true),
      motorRpm: [0, 1, 2, 3].map((motor) =>
        view.getFloat64(TelemetrySlotOffset.motorRpm + motor * 8, true),
      ) as [number, number, number, number],
      flightController: {
        armed: (flags & FcFlag.armed) !== 0,
        mode: enumValue(FC_MODES, view.getInt32(TelemetrySlotOffset.fcMode, true), 'FC mode'),
        activeSetpointKind: enumValue(
          SETPOINT_KINDS,
          view.getInt32(TelemetrySlotOffset.activeSetpointKind, true),
          'active setpoint kind',
        ),
        saturated: (flags & FcFlag.saturated) !== 0,
        crashed: (flags & FcFlag.crashed) !== 0,
        manualLatched: (flags & FcFlag.manualLatched) !== 0,
      },
    },
    targets,
    tracks,
    companion: {
      state: enumValue(
        COMPANION_STATES,
        view.getInt32(TelemetrySlotOffset.companionState, true),
        'companion state',
      ),
      selectedTrackId: selectedTrackId === NULL_TRACK_ID ? null : selectedTrackId,
    },
    unstable: view.getUint32(TelemetrySlotOffset.unstable, true) !== 0,
    checksum: view.getUint32(TelemetrySlotOffset.checksum, true),
  };
};

/** Independently recompute the checksum represented by a decoded snapshot. */
export const checksumTelemetrySnapshot = (snapshot: TelemetrySnapshot): number => {
  const buffer = new ArrayBuffer(TELEMETRY_SLOT_BYTES);
  const bytes = new Uint8Array(buffer);
  encodePayload(new DataView(buffer), bytes, snapshot);
  return fnv1a(bytes.subarray(0, TelemetrySlotOffset.checksum));
};

/** Worker-writer/main-reader seqlock over two fixed telemetry slots. */
export class TelemetryBuffer {
  private readonly buffer: StateBuffer;
  private readonly header: Int32Array;
  private readonly maxReadRetries: number;

  constructor(buffer: StateBuffer, options: TelemetryBufferOptions = {}) {
    if (buffer.byteLength !== TELEMETRY_BUFFER_SIZE) {
      throw new RangeError(`telemetry buffer must be exactly ${TELEMETRY_BUFFER_SIZE} bytes`);
    }
    const maxReadRetries = options.maxReadRetries ?? DEFAULT_SEQLOCK_RETRIES;
    if (!Number.isInteger(maxReadRetries) || maxReadRetries <= 0) {
      throw new RangeError('maxReadRetries must be a positive integer');
    }
    this.buffer = buffer;
    this.header = new Int32Array(buffer, 0, TELEMETRY_HEADER_BYTES / 4);
    this.maxReadRetries = maxReadRetries;
  }

  write(state: SimState): void {
    const sequenceIndex = TelemetryHeaderOffset.sequence / 4;
    const activeIndex = TelemetryHeaderOffset.activeSlot / 4;
    const oddSequence = Atomics.add(this.header, sequenceIndex, 1) + 1;
    try {
      const inactiveSlot = 1 - Atomics.load(this.header, activeIndex);
      const byteOffset = telemetrySlotByteOffset(inactiveSlot);
      const bytes = new Uint8Array(this.buffer, byteOffset, TELEMETRY_SLOT_BYTES);
      bytes.fill(0);
      const view = new DataView(this.buffer, byteOffset, TELEMETRY_SLOT_BYTES);
      encodePayload(view, bytes, snapshotFromState(state));
      view.setUint32(
        TelemetrySlotOffset.checksum,
        fnv1a(bytes.subarray(0, TelemetrySlotOffset.checksum)),
        true,
      );
      Atomics.store(this.header, activeIndex, inactiveSlot);
    } finally {
      Atomics.store(this.header, sequenceIndex, oddSequence + 1);
    }
  }

  read(): TelemetrySnapshot | null {
    const sequenceIndex = TelemetryHeaderOffset.sequence / 4;
    const activeIndex = TelemetryHeaderOffset.activeSlot / 4;
    for (let attempt = 0; attempt < this.maxReadRetries; attempt += 1) {
      const before = Atomics.load(this.header, sequenceIndex);
      if ((before & 1) !== 0) continue;
      const activeSlot = Atomics.load(this.header, activeIndex);
      if (activeSlot !== 0 && activeSlot !== 1) continue;

      const local = new ArrayBuffer(TELEMETRY_SLOT_BYTES);
      new Uint8Array(local).set(
        new Uint8Array(this.buffer, telemetrySlotByteOffset(activeSlot), TELEMETRY_SLOT_BYTES),
      );
      const after = Atomics.load(this.header, sequenceIndex);
      if (before !== after || (after & 1) !== 0) continue;

      const bytes = new Uint8Array(local);
      const view = new DataView(local);
      const committedChecksum = view.getUint32(TelemetrySlotOffset.checksum, true);
      if (committedChecksum !== fnv1a(bytes.subarray(0, TelemetrySlotOffset.checksum))) continue;
      try {
        return decodePayload(view, bytes);
      } catch {
        return null;
      }
    }
    return null;
  }
}
